import { useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import useSWR from 'swr';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { Body2 } from '@/components/@IntranetLibrary/Typography';
import { PLACEHOLDER_COLOR } from '@/components/@IntranetLibrary/DatePicker/styles';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  buildSalesContractHtml,
  SalesContractSnapshot,
  type SalesContractTarget,
} from '@/components/Approval/@components/SalesContract';
import SearchableSelect, {
  type SearchableSelectOption,
} from '@/components/Approval/@components/SearchableSelect';
import type { ReplaceContentApi } from '@/components/LexicalEditor/plugins/ReplaceContentPlugin';
import { getSalesContractDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildSalesContractEditorContent,
  buildSalesContractLabel,
  buildSalesContractSubject,
  pickSalesContractCard,
  type SalesContractCandidate,
  type SalesContractCard,
  type SalesContractDraftSource,
  type SalesContractItem,
} from '@/utils/approval/salesContract';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

const CONTROL_HEIGHT = 44;

type Props = { isEditMode: boolean; editorApi: ReplaceContentApi | null };

/**
 * GERP Sales Contract(136) — 내 미상신 계약을 여러 개 체크하면 계약마다 카드를 불러옴.
 * 고르거나 뺄 때마다 제목·본문을 덮어씀. 편집은 계약을 바꿀 수 없고 저장 카드만
 */
const SalesContractField = ({ isEditMode, editorApi }: Props) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, setValue } = useFormContext<ApprovalFormValues>();
  const saved = useWatch({ control, name: 'salesContract' });
  const addedModalRef = useRef<ModalRef>(null);

  /** 고른 순서 그대로 */
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [cardsById, setCardsById] = useState<Record<string, SalesContractCard>>(
    {},
  );
  const [loadingIds, setLoadingIds] = useState<string[]>([]);
  // 카드 응답(비동기)이 최신 선택·카드를 보도록 state 와 같이 둠
  const selectedRef = useRef<string[]>([]);
  const cardsRef = useRef<Record<string, SalesContractCard>>({});

  const { data: source, isLoading } = useSWR<SalesContractDraftSource>(
    isEditMode ? null : getSalesContractDraftSourceUrl(),
    getNestedDataFetcher,
    {
      revalidateOnFocus: false,
      onError: () =>
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') }),
    },
  );
  const candidates = source?.display.candidates ?? [];

  const candidateById = useMemo(
    () =>
      new Map<string, SalesContractCandidate>(
        (source?.display.candidates ?? []).map((c) => [c.aprvId, c]),
      ),
    [source],
  );

  const labelOf = (id: string) => {
    const c = candidateById.get(id);
    return c ? buildSalesContractLabel(c) : id;
  };

  const toItems = (
    ids: string[],
    loaded: Record<string, SalesContractCard>,
  ): SalesContractItem[] =>
    ids.flatMap((id) =>
      loaded[id]
        ? [
            {
              aprvId: id,
              label: labelOf(id),
              aprvRev: candidateById.get(id)?.aprvRev?.trim() ?? '',
              card: loaded[id],
            },
          ]
        : [],
    );

  /** 카드를 다 받았을 때만 저장 값 — 덜 받았으면 상신 시 안내 */
  const syncSaved = (
    ids: string[],
    loaded: Record<string, SalesContractCard>,
  ) => {
    const first = candidateById.get(ids[0] ?? '');
    const isReady = !!first && ids.every((id) => loaded[id]);
    setValue(
      'salesContract',
      isReady
        ? {
            systemId: first.sysId,
            approvalTypeCode: first.aprvTypcd,
            approvalId: ids.join(','),
            content: buildSalesContractHtml(toItems(ids, loaded), t),
          }
        : null,
      { shouldDirty: true },
    );
  };

  /** 레거시 change_title_content — 고르거나 뺄 때마다 제목·본문 덮어씀 */
  const overwriteSubjectAndBody = (ids: string[]) => {
    const labels = ids.map(labelOf);
    setValue('subject', buildSalesContractSubject(labels, t), {
      shouldDirty: true,
    });
    editorApi?.replace(buildSalesContractEditorContent(labels, t));
  };

  const update = (ids: string[], loaded: Record<string, SalesContractCard>) => {
    selectedRef.current = ids;
    cardsRef.current = loaded;
    setSelectedIds(ids);
    setCardsById(loaded);
  };

  const loadCard = async (id: string) => {
    setLoadingIds((prev) => [...prev, id]);
    try {
      const data = await getNestedDataFetcher<SalesContractDraftSource>(
        getSalesContractDraftSourceUrl([id]),
      );
      const card = pickSalesContractCard(data, id);
      if (!card) throw new Error('card missing');
      // 응답 전에 체크를 풀었으면 버림
      if (!selectedRef.current.includes(id)) return;
      const next = { ...cardsRef.current, [id]: card };
      update(selectedRef.current, next);
      syncSaved(selectedRef.current, next);
    } catch {
      if (!selectedRef.current.includes(id)) return;
      // 같은 건을 다시 골라 재시도할 수 있게 선택에서 뺌
      const next = selectedRef.current.filter((k) => k !== id);
      update(next, cardsRef.current);
      overwriteSubjectAndBody(next);
      syncSaved(next, cardsRef.current);
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      setLoadingIds((prev) => prev.filter((k) => k !== id));
    }
  };

  const applySelection = (nextIds: string[]) => {
    const added = nextIds.filter((id) => !selectedRef.current.includes(id));
    const loaded = { ...cardsRef.current };
    selectedRef.current
      .filter((id) => !nextIds.includes(id))
      .forEach((id) => delete loaded[id]);
    update(nextIds, loaded);
    overwriteSubjectAndBody(nextIds);
    syncSaved(nextIds, loaded);
    added.forEach(loadCard);
  };

  // 레거시 DelCost — 확인 없이 바로 뺌
  const handleDelete = ({ itemId }: SalesContractTarget) =>
    applySelection(selectedRef.current.filter((id) => id !== itemId));

  if (isEditMode)
    return (
      <FieldBlock>
        {saved?.content && <SalesContractSnapshot content={saved.content} />}
      </FieldBlock>
    );

  const options: SearchableSelectOption[] = candidates.map((c) => {
    const label = buildSalesContractLabel(c);
    return {
      value: c.aprvId,
      label,
      labelNode: (
        <OptionLabel>
          <Checkbox
            size="small"
            checked={selectedIds.includes(c.aprvId)}
            tabIndex={-1}
            sx={{ p: 0, pointerEvents: 'none' }}
          />
          {label}
        </OptionLabel>
      ),
    };
  });
  const placeholder = t('결재할 건을 선택해주세요');
  const items = toItems(selectedIds, cardsById);

  return (
    <FieldBlock>
      <ControlRow>
        <SearchableSelect
          multiple
          value={selectedIds}
          options={options}
          placeholder={isLoading ? t('불러오는 중...') : placeholder}
          noOptionsText={isLoading ? null : t('진행할 내역이 없습니다.')}
          // 레거시 콤보처럼 고른 건 대신 안내 문구 — 고른 건은 아래 카드
          renderValue={() => <Placeholder>{placeholder}</Placeholder>}
          onChange={applySelection}
          sx={{
            ...approvalOutlineSelectSx,
            flex: 1,
            minWidth: 0,
            height: CONTROL_HEIGHT,
          }}
        />
        {loadingIds.length > 0 && <Spinner theme="black" size="16px" />}
        <BoxButton
          type="button"
          variant="secondary"
          size="medium"
          onClick={() => addedModalRef.current?.open()}
        >
          {t('추가 내역 확인')}
        </BoxButton>
      </ControlRow>
      {items.length > 0 && (
        <SalesContractSnapshot
          content={buildSalesContractHtml(items, t)}
          onDelete={handleDelete}
        />
      )}
      <Modal
        ref={addedModalRef}
        title={t('추가 내역 확인')}
        size="x-small"
        handleClose={() => addedModalRef.current?.close()}
      >
        <AddedList>
          {selectedIds.map((id) => (
            <Body2 key={id} component="li">
              {labelOf(id)}
            </Body2>
          ))}
        </AddedList>
      </Modal>
    </FieldBlock>
  );
};

const ControlRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
    '& > .MuiInputBase-root, & > .MuiFormControl-root': { flexBasis: '100%' },
  },
}));

const OptionLabel = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const Placeholder = styled('span')({ color: PLACEHOLDER_COLOR });

const AddedList = styled('ul')({
  margin: 0,
  padding: 0,
  listStyle: 'none',
  '& > li + li': { marginTop: 4 },
});

export default SalesContractField;
